import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Play, FileText, ArrowRight, AlertCircle, Search, MousePointerClick, ShieldCheck, Zap, Wallet } from 'lucide-react';

/*
 * /how-a-dispute-works: plain-English six-step walk-through of what
 * actually happens when a buyer files an on-chain dispute in Kojiru.
 * Written at a 12th-grade reading level. No jargon.
 */

const STEPS = [
  {
    n: 1,
    icon: AlertCircle,
    title: 'You (or your agent) notice something is wrong',
    body: 'Concrete example: you told your agent to book a hotel room under $200. The agent booked a $600 room. Or: your agent bought a real-time stock data feed that turned out to be 6 seconds behind reality.',
    accent: 'text-rose-300 border-rose-500/40 bg-rose-500/[0.06]',
  },
  {
    n: 2,
    icon: Search,
    title: 'Your agent gathers the evidence',
    body: 'Your agent already has all of this saved automatically: it does not have to hunt for anything:',
    bullets: [
      'A copy of what you originally authorized (your KYPA credential: the signed permission slip)',
      'A copy of what the seller actually delivered (the receipt, the file, the booking confirmation)',
      'The clock times of both events (when the transaction happened, when the seller was supposed to deliver, when they actually did)',
      'The health-monitor\u2019s signed report if the seller\u2019s service was down or lagging',
    ],
    footer: 'Your agent runs a small piece of math on the delivered thing that produces the Merkle root: a 64-character fingerprint. If anyone changes even one letter of what the seller sent, the fingerprint changes. That\u2019s what makes the proof "cryptographic."',
    accent: 'text-slate-300 border-white/15 bg-white/[0.02]',
  },
  {
    n: 3,
    icon: MousePointerClick,
    title: 'You press one button',
    body: 'In the Kojiru interface, this is a single button that reads something like "File on-chain dispute." On the buyer-agent side, in the reference code, it is literally two lines:',
    code: `proof = kojiru.build_dispute_proof(order_id)   # step 2 automated
tx    = kojiru.file_dispute(proof)             # step 3 button`,
    footer: 'That\u2019s the whole thing on your side. No forms. No customer service line. No 40-day wait.',
    accent: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.05]',
  },
  {
    n: 4,
    icon: ShieldCheck,
    title: 'The oracle checks the math (~8 seconds)',
    body: 'A smart contract called KojiruACSOracle reads your evidence, checks the fingerprint against what the seller signed at delivery, and asks one question: does the delivered thing match the authorized thing?',
    verdict: [
      { yes: 'If yes', text: 'dispute rejected (the seller kept their promise).' },
      { yes: 'If no', text: 'dispute accepted, and the oracle signs its own verdict.', highlight: true },
    ],
    accent: 'text-slate-300 border-white/15 bg-white/[0.02]',
  },
  {
    n: 5,
    icon: Zap,
    title: 'The verdict is recorded against the public pledge',
    body: 'The oracle\u2019s verdict is written to the public record next to the seller\u2019s pledge, the commitment the seller posted before they started selling. Kojiru does not decide or pay any remedy. Any refund comes from the merchant\u2019s own dispute policy and the parties involved.',
    footer: 'A public record of the commitment. Not insurance. Not a promise of repayment.',
    accent: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.05]',
  },
  {
    n: 6,
    icon: Wallet,
    title: 'The record follows the seller',
    body: 'Your evidence stays on the record for your dispute with the merchant. The seller\u2019s Agent Credit Score drops. Their pledge tier may downgrade. Everyone in the KYPA directory sees the breach record forever.',
    accent: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.05]',
  },
];

export default function HowDisputeWorks() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="how-a-dispute-works-page">
      <Helmet>
        <title>How a dispute actually works · Kojiru</title>
        <meta name="description" content="Six steps. About 15 seconds. One human button-press. A plain-English walk-through of what happens when a buyer files an on-chain dispute in the Kojiru recourse layer." />
        <meta property="og:title" content="How a dispute actually works · Kojiru" />
        <meta property="og:description" content="Six steps. About 15 seconds. One human button-press. The Kojiru dispute flow, explained without jargon." />
        <meta property="og:type" content="article" />
        <link rel="alternate" type="application/ld+json" href="/kojiru-schema.jsonld" />
        <link rel="alternate" type="application/json" href="/.well-known/kypa" title="KYPA Protocol Manifest" />
      </Helmet>

      <Navbar />

      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-16 overflow-hidden" data-testid="dispute-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/6 w-[500px] h-[500px] bg-emerald-600/[0.06] rounded-full blur-[150px]" />
        </div>
        <div className="container mx-auto px-6 relative z-10 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[11px] tracking-[3px] text-emerald-300 font-bold uppercase">Explained in plain English</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6" data-testid="dispute-hero-heading">
              How a dispute<br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">actually works.</span>
            </h1>

            <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mb-3">
              Six steps. About 15 seconds. <span className="text-white font-bold">One human button-press.</span>
            </p>
            <p className="text-base text-slate-400 leading-relaxed max-w-3xl">
              The good news: you don&rsquo;t do most of this yourself. Your buying agent does the paperwork.
              You do two things: press a button, and (later) follow up with the merchant using the evidence on record. Here&rsquo;s the full flow
              at the level of a physical checklist.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── STEPS ─────────────────────────────────────────────────── */}
      <section className="pb-20" data-testid="dispute-steps">
        <div className="container mx-auto px-6 max-w-4xl space-y-6">
          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.05 * step.n }}
                className={`rounded-2xl border p-6 sm:p-8 ${step.accent}`}
                data-testid={`dispute-step-${step.n}`}
              >
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/40 border border-white/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[3px] font-bold uppercase opacity-80 mb-1">Step {step.n}</div>
                    <h2 className="text-xl sm:text-2xl font-black text-white leading-tight mb-3">{step.title}</h2>
                    <p className="text-slate-300 leading-relaxed mb-3">{step.body}</p>

                    {step.bullets && (
                      <ul className="space-y-2 my-4">
                        {step.bullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                            <span className="text-emerald-400 mt-1.5 flex-shrink-0"></span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {step.code && (
                      <pre className="bg-black/60 border border-white/10 rounded-lg p-4 my-4 text-sm text-emerald-300 font-mono overflow-x-auto leading-relaxed">
                        <code>{step.code}</code>
                      </pre>
                    )}

                    {step.verdict && (
                      <div className="grid sm:grid-cols-2 gap-3 my-4">
                        {step.verdict.map((v, i) => (
                          <div
                            key={i}
                            className={`px-4 py-3 rounded-lg border ${v.highlight ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-white/15 bg-white/[0.03]'}`}
                          >
                            <div className={`text-[10px] tracking-[2px] font-bold uppercase mb-1 ${v.highlight ? 'text-emerald-300' : 'text-slate-400'}`}>{v.yes}</div>
                            <div className="text-sm text-white">{v.text}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {step.footer && (
                      <p className="text-sm text-slate-400 italic leading-relaxed mt-3">{step.footer}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ─── ONE-SENTENCE SUMMARY ─────────────────────────────────── */}
      <section className="py-16 border-t border-white/5" data-testid="dispute-summary">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <div className="text-[11px] tracking-[3px] text-emerald-400 font-bold uppercase mb-4">The one-sentence version</div>
          <p className="text-2xl sm:text-3xl font-black text-white leading-[1.25]">
            A bad outcome in agentic commerce should leave
            <span className="text-emerald-400"> a public record anyone can check</span>. The seller posted a pledge
            before they were allowed to sell. That pledge is a public commitment signal, not a deposit held for you,
            and not a promise of repayment.
          </p>
        </div>
      </section>

      {/* ─── CTA ───────────────────────────────────────────────────── */}
      <section className="py-16 border-t border-white/5" data-testid="dispute-cta">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/manifesto"
              data-testid="dispute-cta-manifesto"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-black font-bold hover:bg-slate-200 transition-colors"
            >
              <Play className="w-5 h-5 fill-current" />
              Watch the 45-second manifesto
            </Link>
            <a
              href="/downloads/kojiru-two-moments-mandate-outcome.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dispute-cta-paper"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-emerald-500/40 bg-emerald-500/5 text-emerald-300 font-bold hover:bg-emerald-500/10 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Read the full paper
            </a>
            <Link
              to="/for-merchants"
              data-testid="dispute-cta-merchants"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/20 text-white font-bold hover:bg-white/5 transition-colors"
            >
              For merchants <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
